import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, Container, Grid, MenuItem, TextField, Tooltip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, IconButton, InputAdornment } from '@mui/material';
import { toast } from 'react-toastify';
import { DataGrid } from '@mui/x-data-grid';
import { Close, Done, Filter3, Print, Clear } from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import moment from 'moment';

import PageDefault from '../../components/PageDefault';
import api from '../../services/api';
import apiDAM from '../../services/apiDAM';
import { getFormatador, getYears, situacao_dam, tipo_dam } from '../../services/helper';
import { hasAnyRole } from '../../services/auth';

export default function ListChargers() {
    const [cobrancas, setCobrancas] = React.useState([]);
    const [situacaoForm, setSituacaoForm] = React.useState('todas');
    const [anoForm, setAnoForm] = React.useState(moment().format('YYYY'));
    const [tipoForm, setTipoForm] = React.useState('todos');
    const [nameForm, setNameForm] = React.useState('');
    const [numForm, setNumForm] = React.useState('');
    const [searchTerm, setSearchTerm] = React.useState('');
    const [numSearch, setNumSearch] = React.useState('');
    const [carregando, setCarregando] = React.useState(false);
    const [selecionados, setSelecionados] = React.useState([]);
    const [paginationModel, setPaginationModel] = React.useState({
      page: 0,
      pageSize: 20,
    });
    const [totalElements, setTotalElements] = React.useState(0);
    const [dialogType, setDialogType] = React.useState('');
    const [dialogMsg, setDialogMsg] = React.useState('');
    const [openDialog, setOpenDialog] = React.useState(false);

    const canManageCobrancas = hasAnyRole(['ADMIN']);

    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const contribuinteID = queryParams.get('contribuinte');

    const getInfos = (cobranca) => {
        const formatador = getFormatador();
        
        const { id, contribuinte, endereco, valorTotal, situacao, valorPago, tipo, numDoc, bc, valorApurado, valorJuros, valorMulta } = cobranca;
                
        const endereco_aux = endereco ? endereco : contribuinte.endereco;
        const numero = endereco_aux.numero === 0 ? 'S/N' : endereco_aux.numero;
        const complemento = endereco_aux.complemento ? `(${endereco_aux.complemento})` : '';

        return {
            id,
            numDoc,
            contribuinte: contribuinte.nome,
            documento: contribuinte.cpfCnpj.length === 11 ? contribuinte.cpfCnpj.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.***.***-$4") : contribuinte.cpfCnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5"),
            endereco: `${endereco_aux.rua}, ${numero}, ${endereco_aux.bairro} ${complemento}`,
            valorDevido: formatador.format(valorTotal),
            valorPago: valorPago ? formatador.format(valorPago) : '',
            valorApurado: valorApurado ? formatador.format(valorApurado) : '',
            valorJuros: valorJuros ? formatador.format(valorJuros) : '',
            valorMulta: valorMulta ? formatador.format(valorMulta) : '',
            situacao,
            tipo,
            bc: bc ? formatador.format(bc) : ''
        };
    }

    const callAPI = React.useCallback(() => {
        setCarregando(true);

        if (numSearch !== '') {
            api.get(`/cobranca/${numSearch}`)
            .then(res => {
                const aux = [];

                const c = getInfos(res.data);
                aux.push(c);

                setCobrancas(aux);
                setTotalElements(1);
                setCarregando(false);
            })
            .catch(err => {
                if (err.response) {
                    const message = err.response.data.message;
                    toast.error(message);
                }
                setCobrancas([])
                setTotalElements(0);
                setCarregando(false);
            });
        } else {
            let query = contribuinteID ? `contribuinteId=${contribuinteID}&` : '';
            query = query.concat(searchTerm !== '' ? `contribuinte=${searchTerm}&` : '');
            query = query.concat(situacaoForm !== 'todas' ? `situacao=${situacaoForm}&` : '');
            query = query.concat(tipoForm !== 'todos' ? `tipo=${tipoForm}&` : '');
            query = query.concat(`ano=${anoForm}&`);
            
            api.get(`/cobranca?${query}page=${paginationModel.page}&size=${paginationModel.pageSize}`)
            .then(res => {
                const aux = [];
                
                res.data.content.forEach(cobranca => {
                    const c = getInfos(cobranca);
                    aux.push(c);
                });
                setCobrancas(aux);
                setTotalElements(res.data.totalElements);
                setCarregando(false);
            })
            .catch(err => {
                if (err.response) {
                    const message = err.response.data.message;
                    toast.error(message);
                }
                setCobrancas([])
                setCarregando(false);
            });
        }
    }, [paginationModel.page, paginationModel.pageSize, situacaoForm, tipoForm, anoForm, contribuinteID, searchTerm, numSearch])

    React.useEffect(() => {
        callAPI();
    }, [paginationModel.page, paginationModel.pageSize, situacaoForm, tipoForm, anoForm, contribuinteID, searchTerm, numSearch, callAPI]);

    async function cancelChargers() {
        setOpenDialog(false);
        setCarregando(true);

        selecionados.forEach(async i => {
            const status = cobrancas.filter(item => (item.id === i))[0].situacao;
            
            if (['Aberto', 'Vencido'].includes(status)) {
                await api.put(`/cobranca/cancelar/${i}`)
                    .then(res => {
                        toast.success(`Cobrança ${res.data.id}/${res.data.anoReferencia} cancelada com sucesso`);
                    })
                    .catch(err => {
                        toast.error(err.response.data.message || 'Ocorreu um erro, verifique os dados');
                    })
            } else {
                toast.warning(`Situação da DAM ${i} é diferente de Aberto!`)
            }
        })
        setTimeout(() => {
            setCarregando(false);
            callAPI();
        }, 2000);
    }

    function parcelarCobranca() {
        setOpenDialog(false);

        if (selecionados.length !== 1) {
            toast.error('Selecione exatamente 1 cobrança para parcelar');
            return;
        }

        const cobrancaId = selecionados[0];
        setCarregando(true);

        api.put(`/cobranca/parcelar/${cobrancaId}`)
        .then(res => {
            const parcelas = Array.isArray(res.data) ? res.data : [];
            const idsParcelas = parcelas.map(p => p && p.id).filter(Boolean);

            if (idsParcelas.length === 0) {
                setCarregando(false);
                toast.error('Parcelamento concluído, mas não foi possível identificar as parcelas retornadas');
                callAPI();
                return;
            }

            const dataPy = {
                cobrancas: idsParcelas
            };

            apiDAM.post(`/impressao-massiva?com-aviso=False`, dataPy, {
                headers: {
                    'Content-Type': 'application/json'
                }
            })
            .then(res => {
                setCarregando(false);
                const blob = new Blob([res.data], {
                    type: 'application/pdf'
                });
                const url = window.URL.createObjectURL(blob);
                window.open(url);
                callAPI();
            })
            .catch(err => {
                setCarregando(false);
                const message = err.response?.data?.message || 'Erro ao imprimir parcelas';
                toast.error(message);
                callAPI();
            })
        })
        .catch(err => {
            setCarregando(false);
            toast.error(err.response?.data?.message || 'Ocorreu um erro ao parcelar a cobrança');
        })
    }

    function printChargers(comAviso) {
        setOpenDialog(false);
        setCarregando(true);

        const paraImpressao = []

        cobrancas.forEach(item => {
            if (selecionados.includes(item.id) && ['Aberto', 'Parcial', 'Vencido'].includes(item.situacao))
                paraImpressao.push(item.id)
        });

        if (paraImpressao.length > 0) {
            const dataPy = {
                'cobrancas': paraImpressao
            }
            
            apiDAM.post(`/impressao-massiva?com-aviso=${comAviso}`, dataPy, {
                headers: {
                    'Content-Type': 'application/json'
                }
            })
            .then(res => {
                setCarregando(false);
                const blob = new Blob([res.data], {
                    type: 'application/pdf'
                });
                const url = window.URL.createObjectURL(blob);
                window.open(url);
            })
            .catch(err => {
                setCarregando(false);
                const message = err.response.data.message;
                toast.error(message);
            })
        } else {
            setCarregando(false);
            toast.error('Nenhuma cobrança com valor em aberto selecionada!');
        }
    }

    const columns = [
        { field: 'id', headerName: 'DAM', width: 80 },
        { field: 'tipo', headerName: 'Tipo', width: 70 },
        { field: 'numDoc', headerName: 'N.º Doc', width: 90 },
        { field: 'contribuinte', headerName: 'Contribuinte', width: 250 },
        { field: 'endereco', headerName: 'Endereço', width: 350 },
        { field: 'bc', headerName: 'BC (R$)', width: 100 },
        { field: 'valorApurado', headerName: 'Apurado (R$)', width: 100 },
        { field: 'valorDevido', headerName: 'Valor (R$)', width: 100 },
        { field: 'valorJuros', headerName: 'Juros (R$)', width: 100 },
        { field: 'valorMulta', headerName: 'Multa (R$)', width: 100 },
        { field: 'valorPago', headerName: 'Pago (R$)', width: 100 },
        { field: 'situacao', headerName: 'Status', width: 100 }
    ];

    const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({
        bc: false,
        valorApurado: false,
        valorJuros: false,
        valorMulta: false
    });

    return (
        <PageDefault carregando={false} title='Cobranças Geradas'>
            <Container component="main" maxWidth="sm" sx={{ mb: 0 }}>
                <Grid container spacing={3} mt={2}>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            id="situacao"
                            select
                            fullWidth
                            required
                            label="Situação"
                            value={situacaoForm}
                            onChange={e => {
                                setSituacaoForm(e.target.value);
                                setPaginationModel({
                                    ...paginationModel,
                                    page: 0
                                })
                            }}
                            disabled={carregando || numForm !== ''}
                        >
                            {situacao_dam.map(({ value, label }) => (
                                <MenuItem value={value} key={value}>{label}</MenuItem>    
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            id="anoForm"
                            select
                            fullWidth
                            required
                            label="Ano Referência"
                            value={anoForm}
                            disabled={carregando || numForm !== ''}
                            onChange={e => (
                                setAnoForm(e.target.value)
                            )}
                        >
                            {getYears().map(ano => (
                                <MenuItem value={ano} key={ano}>{ano}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            id="tipo"
                            select
                            fullWidth
                            required
                            label="Tipo"
                            value={tipoForm}
                            onChange={e => {
                                setTipoForm(e.target.value);
                                setPaginationModel({
                                    ...paginationModel,
                                    page: 0
                                })
                            }}
                            disabled={carregando || numForm !== ''}
                        >
                            <MenuItem value='todos'>Todos</MenuItem>
                            <MenuItem value='IPTU'>IPTU</MenuItem>
                            {tipo_dam.map(({valor, label}) => (
                                <MenuItem value={valor} key={valor}>{label}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    {canManageCobrancas ? (
                    <>
                        <Grid item xs={12} sm={3}>
                            <TextField
                                id="num-dam"
                                name="num-dam"
                                label="N.º DAM"
                                type='number'
                                value={numForm}
                                onChange={e => setNumForm(e.target.value)}
                                InputProps={{
                                    endAdornment: numForm ? (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="Limpar N.º DAM"
                                                edge="end"
                                                size="small"
                                                onClick={() => {
                                                    setNumForm('');
                                                    setNumSearch('');
                                                    setPaginationModel({
                                                        ...paginationModel,
                                                        page: 0
                                                    });
                                                }}
                                            >
                                                <Clear fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : null
                                }}
                                variant='standard'
                                fullWidth
                                sx={{
                                    mr: 1,
                                    '& input[type=number]': {
                                        MozAppearance: 'textfield'
                                    },
                                    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                                        WebkitAppearance: 'none',
                                        margin: 0
                                    }
                                }}
                                />
                        </Grid>
                        <Grid item xs={12} sm={9}>
                            <TextField
                                id="search"
                                name="search"
                                label="Nome do contribuinte"
                                value={nameForm}
                                onChange={e => setNameForm(e.target.value)}
                                InputProps={{
                                    endAdornment: nameForm ? (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="Limpar nome do contribuinte"
                                                edge="end"
                                                size="small"
                                                onClick={() => {
                                                    setNameForm('');
                                                    setSearchTerm('');
                                                    setPaginationModel({
                                                        ...paginationModel,
                                                        page: 0
                                                    });
                                                }}
                                            >
                                                <Clear fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : null
                                }}
                                variant='standard'
                                fullWidth
                                sx={{mr: 1}}
                                disabled={carregando || numForm !== ''}
                                />
                        </Grid>
                    </>
                    ) : null}
                </Grid>
                {canManageCobrancas ? (
                    <Button 
                    fullWidth
                    variant="contained"
                    sx={{ mt: 3, mb: 2 }}
                    startIcon={<SearchIcon />} onClick={() => {
                    setSearchTerm(nameForm);
                    setNumSearch(numForm);
                    setPaginationModel({
                        ...paginationModel,
                        page: 0
                    });
                    }}>Pesquisar</Button>
                ) : null}
            </Container>
            <div>
                {canManageCobrancas ? (
                    <>
                        <Tooltip title="Pagar cobrança">
                            <span>
                                <Button startIcon={<Done />} disabled={selecionados.length !== 1 || ['Cancelado', 'Pago'].includes(situacaoForm)} onClick={() => {
                                    const status = cobrancas.filter(item => (item.id === selecionados[0]))[0].situacao;
                                    if (['Parcial', 'Aberto', 'Vencido'].includes(status)) {
                                        navigate(`/pagar-cobranca?numero=${selecionados[0]}`)
                                    } else {
                                        toast.warning('Só é possível pagar uma cobrança com valor em aberto!')
                                    }
                                }}>
                                    Pagar
                                </Button>
                            </span>
                        </Tooltip>
                        <Tooltip title="Parcelar cobrança (Acréscimo de 30%)">
                            <span>
                                <Button startIcon={<Filter3 />} disabled={selecionados.length !== 1 || ['Cancelado', 'Pago', 'Parcelado'].includes(situacaoForm)} onClick={() => {
                                    const cobrancaId = selecionados[0];
                                    setCarregando(true);

                                    api.get(`/cobranca/${cobrancaId}`)
                                    .then(res => {
                                        const tipo = res.data.tipo;

                                        if (tipo !== 'IPTU') {
                                            setCarregando(false);
                                            toast.error('Só é possível parcelar IPTU!');
                                            return;
                                        }

                                        const situacao = res.data.situacao;

                                        if (['Pago', 'Cancelado', 'Parcelado'].includes(situacao)) {
                                            setCarregando(false);
                                            toast.error('Só é possível parcelar IPTU com saldo!');
                                            return;
                                        }

                                        const imovelID = res.data.ImovelId;
                                        const contribuinteID = res.data.contribuinte.id;

                                        // recuperar o valor do iptu sem desconto a vista..
                                        setCarregando(true);
                                        api.get(`/contribuinte/${contribuinteID}/imovel/${imovelID}/imposto?isAvista=false`)
                                        .then(res2 => {
                                            
                                            const valorAtual         = res.data.valorTotal;
                                            const impostoSemDesconto = res2.data.valor.toFixed(2);
                                            const valorJuros         = res.data.valorJuros ? res.data.valorJuros : 0;
                                            const valorMulta         = res.data.valorMulta ? res.data.valorMulta : 0;
                                            const valorPago          = res.data.valorPago ? res.data.valorPago : 0;

                                            // console.log('valor atual', valorAtual);
                                            // console.log('valor do imposto parcelado', impostoSemDesconto);
                                            // console.log('valor dos juros', valorJuros);
                                            // console.log('valor da multa', valorMulta);
                                            // console.log('valor pago', valorPago);
                                            
                                            const novoValorImposto = parseFloat(impostoSemDesconto) + parseFloat(valorJuros) + parseFloat(valorMulta) - parseFloat(valorPago);
                                            
                                            // console.log('novo valor do imposto', novoValorImposto);

                                            const valorParcelas = novoValorImposto / 3;
                                            // console.log('valor das parcelas', valorParcelas);

                                            setDialogType('parcelarCobranca');
                                            setDialogMsg(
                                                `Deseja realmente parcelar a DAM ${selecionados[0]}? (R$ ${valorAtual.toFixed(2).replace('.', ',')})` +
                                                `\n\nNovo valor total: R$ ${novoValorImposto.toFixed(2).replace('.', ',')} (3x R$ ${valorParcelas.toFixed(2).replace('.', ',')})` + 
                                                `\nJuros: R$ ${valorJuros.toFixed(2).replace('.', ',')}` + 
                                                `\nMulta: R$ ${valorMulta.toFixed(2).replace('.', ',')}` + 
                                                `\nJá pago: R$ ${valorPago.toFixed(2).replace('.', ',')}`
                                            );
                                            setCarregando(false);
                                            setOpenDialog(true);
                                        })
                                        .catch(err => {
                                            setCarregando(false);
                                            if (err.response) {
                                                const message = err.response.data.message;
                                                toast.error(message);
                                            }
                                        });

                                        console.log(res.data);
                                    })
                                    .catch(() => {
                                        setCarregando(false);
                                        toast.error('Erro ao buscar cobrança!');
                                    })
                                }}>
                                    Parcelar
                                </Button>
                            </span>
                        </Tooltip>
                        <Tooltip title="Cancelar cobrança">
                            <span>
                                <Button startIcon={<Close />} disabled={selecionados.length === 0 || ['Cancelado', 'Pago'].includes(situacaoForm)} 
                                    onClick={() => {
                                        setDialogType('cancelarCobranca');
                                        setDialogMsg(`Deseja realmente cancelar as cobranças selecionadas ${selecionados}? *Essa ação é irreversível`);
                                        setOpenDialog(true);
                                }}>
                                    Cancelar
                                </Button>
                            </span>
                        </Tooltip>
                    </>
                ) : null}
                <Tooltip title="Imprimir cobrança">
                    <span>
                        <Button startIcon={<Print />} disabled={selecionados.length === 0 || ['Cancelado', 'Pago'].includes(situacaoForm)} onClick={() => {
                            setDialogType('imprimirCobranca');
                            setDialogMsg(`Deseja imprimir as cobranças com aviso?`);
                            setOpenDialog(true);
                        }}>
                            Imprimir
                        </Button>
                    </span>
                </Tooltip>
            </div>
            <Box sx={{ height: 600, width: '100%' }}>
                <DataGrid
                    loading={carregando}
                    rows={cobrancas}
                    columnVisibilityModel={columnVisibilityModel}
                    onColumnVisibilityModelChange={(newModel) =>
                        setColumnVisibilityModel(newModel)
                    }
                    columns={columns}
                    paginationMode='server'
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    pageSizeOptions={[paginationModel.pageSize]}
                    rowCount={totalElements}
                    //keepNonExistentRowsSelected
                    checkboxSelection
                    onRowSelectionModelChange={(a) => {
                        setSelecionados(a);
                    }}
                    localeText={{
                        noRowsLabel: 'Sem cobranças encontradas'
                    }}
                    showToolbar
                />
            </Box>
            <Dialog
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    Confirmação de Ação
                </DialogTitle>
                <DialogContent>
                <DialogContentText id="alert-dialog-description" sx={{ whiteSpace: 'pre-line' }}>
                    {dialogMsg}
                </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>

                    {dialogType === 'cancelarCobranca' && <div>
                        <Button onClick={cancelChargers} autoFocus>
                            Continuar
                        </Button>
                    </div>}
                    {dialogType === 'imprimirCobranca' && <div>
                        <Button onClick={() => printChargers(true)} autoFocus>
                            Sim
                        </Button>
                        <Button onClick={() => printChargers(false)}>
                            Não
                        </Button>
                    </div>}

                    {dialogType === 'parcelarCobranca' && <div>
                        <Button onClick={parcelarCobranca} autoFocus>
                            Continuar
                        </Button>
                    </div>}
                </DialogActions>
            </Dialog>
        </PageDefault>
    );
}